import { useEffect } from 'react';

import { getAbsoluteDateFromPlayerTime } from '@/utils/hh-mm-ss-into-number';

type TUseCutFormShortcutsParams = {
  segmentStartDatetime?: string | null;
  playerVideoCurrentTime: number;
  onExit: () => void;
  onSetEnd: (datetime: Date) => void;
  onClearEnd: () => void;
  onEraseDescription: () => void;
};

/**
 * Escape and F8 act on the form alone, so they must stay above the
 * `segmentStartDatetime` guard. F9 is below it because it needs the player
 * instant; F4 only shares that guard because it always has, not because it needs
 * a segment.
 */
function useCutFormShortcuts({
  segmentStartDatetime,
  playerVideoCurrentTime,
  onExit,
  onSetEnd,
  onClearEnd,
  onEraseDescription,
}: TUseCutFormShortcutsParams) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl + F9 belongs to the rótulo form and Ctrl + F12 to incident reporting;
      // matching on the bare key would fire this on those too.
      if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        onExit();
        return;
      }

      if (event.key === 'F8') {
        event.preventDefault();
        onClearEnd();
        return;
      }

      if (event.key !== 'F9' && event.key !== 'F4') return;
      if (!segmentStartDatetime) return;

      event.preventDefault();

      if (event.key === 'F9') {
        onSetEnd(getAbsoluteDateFromPlayerTime(segmentStartDatetime, playerVideoCurrentTime));
        return;
      }

      onEraseDescription();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    segmentStartDatetime,
    playerVideoCurrentTime,
    onExit,
    onSetEnd,
    onClearEnd,
    onEraseDescription,
  ]);
}

export { useCutFormShortcuts };
